import { css } from '@emotion/css';
import { xocPlate } from 'constants/global.style';
import { useEffect, useRef, useState } from 'react';
import txcbFunction from './txcb-function';

interface Props {
  timerCount: string;
  result: number[] | null;
  isPlateClose: boolean;
  onToggleHand: () => void;
  onOpenWin: (result: number[]) => void;
}

type Phase = 'RESET' | 'XOC' | 'STOP' | 'OPEN_AUTO' | 'OPEN_MANUAL';

export default function PlateBowlTXCB(props: Props) {
  const { timerCount, result, isPlateClose, onToggleHand, onOpenWin } = props;

  const timerRef = useRef<HTMLDivElement>(null);
  const bowRef = useRef<HTMLDivElement>(null);
  const plateBowRef = useRef<HTMLDivElement>(null);

  // refs cho trạng thái bowl
  const currentX = useRef(0);
  const currentY = useRef(0);
  const initialX = useRef(0);
  const initialY = useRef(0);
  const xOffset = useRef(0);
  const yOffset = useRef(0);
  const onceCall = useRef(false);
  const activeMove = useRef(false);

  const [showHotTimer, setShowHotTimer] = useState(false);
  const [currentResult, setCurrentResult] = useState<number[]>([1, 1, 1]);
  const [phase, setPhase] = useState<Phase>('RESET');
  const [showTimer, setShowTimer] = useState(false);

  // reset bowl state
  const resetBowState = () => {
    currentX.current = 0;
    currentY.current = 0;
    initialX.current = 0;
    initialY.current = 0;
    xOffset.current = 0;
    yOffset.current = 0;
    onceCall.current = false;
    activeMove.current = false;
    if (bowRef.current) {
      bowRef.current.style.transform = 'none';
      bowRef.current.style.transition = 'none';
    }
  };

  // ===== Timer Effect =====
  useEffect(() => {
    setShowHotTimer(Number(timerCount) < 10);
    if (Number(timerCount) < 10 && timerRef.current) {
      timerRef.current.style.transform = 'scale(1.25)';
      setTimeout(() => {
        if (timerRef.current) {
          timerRef.current.style.transform = 'scale(1)';
        }
      }, 160);
    }
    setShowTimer(timerCount !== '00');

    // Khi bắt đầu đếm lại (không phải 00) → reset bowl
    if (timerCount !== '00') {
      setPhase('RESET');
      resetBowState();
      if (bowRef.current) {
        bowRef.current.style.pointerEvents = 'none';
        bowRef.current.style.opacity = '0';
      }
    }
  }, [timerCount]);

  // ===== Result Effect =====
  useEffect(() => {
    if (!result) return;

    // B1: reset
    setPhase('RESET');
    resetBowState();
    if (bowRef.current) {
      bowRef.current.style.pointerEvents = 'auto';
      bowRef.current.style.opacity = '1';
    }
    setShowTimer(false);

    // B2: bắt đầu xóc
    const xocTimer = setTimeout(() => {
      setPhase('XOC');
      setCurrentResult(result);
    }, 500);

    // B3: dừng xóc
    const stopTimer = setTimeout(() => {
      setPhase('STOP');

      if (isPlateClose === false) {
        // Auto open
        setPhase('OPEN_AUTO');
        onOpenWin(result);
      }
    }, 3000);

    return () => {
      clearTimeout(xocTimer);
      clearTimeout(stopTimer);
    };
  }, [result, isPlateClose]);

  // ===== Mouse Handlers (Manual Open) =====
  const onStartBowMouse = (e: React.MouseEvent) => {
    if (phase !== 'STOP' || !isPlateClose) return;
    e.preventDefault();
    initialX.current = e.clientX - xOffset.current;
    initialY.current = e.clientY - yOffset.current;
    activeMove.current = true;
  };

  const onMoveBowMouse = (e: React.MouseEvent) => {
    if (!activeMove.current || phase !== 'STOP' || !isPlateClose) return;
    if (!bowRef.current || !plateBowRef.current) return;
    handleMove(e.clientX, e.clientY);
  };

  const onEndBowMouse = () => {
    activeMove.current = false;
    initialX.current = currentX.current;
    initialY.current = currentY.current;
  };

  // ===== Shared Move Logic =====
  const handleMove = (clientX: number, clientY: number) => {
    if (!bowRef.current || !plateBowRef.current) return;

    const plateRect = plateBowRef.current.getBoundingClientRect();
    const circle2 = {
      x: plateRect.x + plateRect.width / 2,
      y: plateRect.y + plateRect.height / 2,
      radius: 50,
    };

    currentX.current = clientX - initialX.current;
    currentY.current = clientY - initialY.current;
    xOffset.current = currentX.current;
    yOffset.current = currentY.current;

    bowRef.current.style.transform = `translate3d(${currentX.current}px, ${currentY.current}px, 0)`;

    const bowlRect = bowRef.current.getBoundingClientRect();
    const circle1 = {
      x: bowlRect.x + bowlRect.width / 2,
      y: bowlRect.y + bowlRect.height / 2,
      radius: 50,
    };

    if (!txcbFunction.collideCircle(circle1, circle2) && !onceCall.current) {
      onceCall.current = true;

      bowRef.current.style.transition = 'all .4s ease-out';
      bowRef.current.style.opacity = '0';
      bowRef.current.style.pointerEvents = 'none';

      // manual open
      setPhase('OPEN_MANUAL');
      onOpenWin(result || []);

      setTimeout(() => {
        resetBowState();
      }, 410);
    }
  };

  // ===== JSX =====
  return (
    <div className={styles.plateBowlWrap}>
      <div className={`platebow-inner ${phase === 'XOC' ? 'xoc' : ''}`} ref={plateBowRef}>
        <img className="plate-img" src={require(`assets/images/txcanban/plate.png`)} alt="" />

        <div
          className={`
            bowl-img-wrap
            ${phase === 'OPEN_MANUAL' ? 'open' : ''}
            ${phase === 'OPEN_AUTO' ? 'auto' : ''}
          `}
          ref={bowRef}
          {...(isPlateClose && {
            onMouseDown: onStartBowMouse,
            onMouseMove: onMoveBowMouse,
            onMouseUp: onEndBowMouse,
          })}
        >
          <img className="bowl-img" src={require(`assets/images/txcanban/bowl.png`)} alt="bowl" />
        </div>

        <div className={`timer-count ${showTimer ? 'show' : ''}`}>
          <div ref={timerRef} className={`time-count ${showHotTimer ? 'hot-timer' : ''}`}>
            {timerCount}
          </div>
        </div>

        <div className={`result-wrap ${!showTimer || timerCount === '' ? 'show' : ''}`}>
          <div className="result-content">
            {currentResult.map((dice, index) => (
              <img
                key={index}
                className="dice-icon"
                src={require(`assets/images/txcanban/dice-${dice}.png`)}
              />
            ))}
          </div>
        </div>
      </div>

      <button className="btn-reset btn-hand" onClick={onToggleHand}>
        {isPlateClose ? (
          <img className="plate-img" src={require(`assets/images/txcanban/hand-on.png`)} alt="" />
        ) : (
          <img className="plate-img" src={require(`assets/images/txcanban/hand-off.png`)} alt="" />
        )}
      </button>
    </div>
  );
}

const styles = {
  plateBowlWrap: css`
    height: 100%;
    position: relative;
    .platebow-inner {
      z-index: 2;
      position: relative;
      height: 100%;
      &.xoc {
        animation: ${xocPlate} 200ms ease-out infinite;
      }
    }

    .bowl-img-wrap {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      touch-action: none;
      user-select: none;
      z-index: 5;
    }

    .bowl-img-wrap.open {
      transform: translate(80px, -80px) !important;
      opacity: 0;
      transition: all 0.4s ease-out;
    }

    .bowl-img-wrap.auto {
      transform: translate(80px, -80px) !important;
      opacity: 0 !important;
      pointer-events: none !important;
      transition: 0.5s ease-in !important;
    }

    .bowl-img {
      position: absolute;
      top: 0;
      left: 0;
      padding: 4px;
      z-index: 5;
      pointer-events: none;
    }

    .timer-count {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: 0.25s;
      &.show {
        opacity: 1;
      }
      .time-count {
        color: var(--ion-color-secondary);
        font-size: 60px;
        font-weight: 700;
        transition: transform 400ms ease-in-out;
      }
      .hot-timer {
        color: var(--ion-color-primary);
      }
    }

    .result-wrap {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: 0.25s;
      &.show {
        opacity: 1;
      }
      .result-content {
        width: 100%;
        height: 100%;
        position: relative;
        .dice-icon {
          width: 52px;
          position: absolute;
          &:nth-child(1) {
            left: 50%;
            transform: translateX(-50%);
            top: 20px;
          }
          &:nth-child(2) {
            left: 20px;
            bottom: 30px;
          }
          &:nth-child(3) {
            right: 20px;
            bottom: 30px;
          }
        }
      }
    }

    .btn-hand {
      position: absolute;
      bottom: -53px;
      left: 50%;
      transform: translateX(-50%);
      width: 30px;
    }
  `,
};
